import { object } from "../schema-util";

import { architecture } from "./architecture";
import { attachments } from "./attachments";
import { fileViewer } from "./file-viewer";
import { map } from "./map";
import { misc } from "./misc";
import { project } from "./project";
import { report } from "./report";
import { settings } from "./settings";
import { subscription } from "./subscription";

/**
 * This is the schema for all the events that can be sent to the analytics client.
 *
 * These are defined as zod schemas. This allows us to define the shape of the event
 * and validate it at runtime as well as generating types for the events.
 *
 * For all event properties, it is important to avoid any PII or sensitive information.
 * This includes things like
 * - email addresses
 * - names
 * - company specific information (e.g. project name)
 * - any other information that could be used to identify a user, company, or project.
 *
 * The event name should be in the format of "category.subcategory.action" or "category.action".
 * This allows us to easily group events in the analytics dashboard and be deliberate about naming.
 *
 * - Event names should be in lowercase, terms separated by periods, and use dashes for compound terms.
 * - Event properties should be in lowerCamelCase
 * - Event properties should be as specific as possible to the event.
 * - Event properties are allowed to be optional.
 * - Event properties should in most cases be primitive types (string, number, boolean) or an enum thereof.
 *
 * Partial event schemas can be defined separately and then merged into the main schema to
 * improve readability, reusability, and maintainability.
 *
 * The base structure of events is:
 * @example
 * "feature-name.category.namespace.action": event({
 *   prop1: string(),
 *   prop2: number(),
 * }),
 */
export const analyticsEventsSchema = object({
  ...project,
  ...attachments,
  ...fileViewer,
  ...architecture,
  ...misc,
  ...map,
  ...report,
  ...settings,
  ...subscription,
});
